// WP-6.3 (M29 AD-3/6/7, S-32): allocation-suggestion queries/mutations over the engine-backed
// API, mirroring lib/goals.ts's exact hook shape.
import {
  suggestionListResponseSchema,
  suggestionResponseSchema,
  type SuggestionAllocationCreateRequest,
  type SuggestionResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { GOALS_KEY, RESERVES_KEY } from './goals';

export const SUGGESTIONS_KEY = ['suggestions'] as const;

export function useActiveSuggestions(asOf?: string): UseQueryResult<SuggestionResponse[]> {
  const params = new URLSearchParams();
  if (asOf !== undefined) params.set('asOf', asOf);
  const qs = params.toString();
  return useQuery({
    queryKey: [...SUGGESTIONS_KEY, asOf],
    queryFn: async () => {
      const body = suggestionListResponseSchema.parse(
        await api.get(`/api/v1/suggestions${qs === '' ? '' : `?${qs}`}`),
      );
      return body.suggestions;
    },
  });
}

function useInvalidateSuggestions(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: SUGGESTIONS_KEY });
  };
}

export function useProposeAllocationSuggestion(): UseMutationResult<
  SuggestionResponse,
  Error,
  SuggestionAllocationCreateRequest
> {
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: async (input) =>
      suggestionResponseSchema.parse(await api.post('/api/v1/suggestions/allocation', input)),
    onSuccess: invalidate,
  });
}

// Accept creates reserves (AD-7 boundary), which also change their linked goal's derived
// allocatedMinor/remainingMinor (GL-1) — the same cross-invalidation shape lib/goals.ts's own
// useInvalidateReserves already established for goal/reserve cross-effects.
export function useAcceptSuggestion(
  id: string,
): UseMutationResult<SuggestionResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      suggestionResponseSchema.parse(await api.post(`/api/v1/suggestions/${id}/accept`)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SUGGESTIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: RESERVES_KEY }),
        queryClient.invalidateQueries({ queryKey: GOALS_KEY }),
      ]);
    },
  });
}

// AD-6: snoozable — a required future resurface date computed client-side from one of a few
// common windows (see Advisor.tsx); no full date picker for this MVP.
export function useSnoozeSuggestion(
  id: string,
): UseMutationResult<SuggestionResponse, Error, string> {
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: async (snoozedUntil: string) =>
      suggestionResponseSchema.parse(
        await api.post(`/api/v1/suggestions/${id}/snooze`, { snoozedUntil }),
      ),
    onSuccess: invalidate,
  });
}

export function useDismissSuggestion(
  id: string,
): UseMutationResult<SuggestionResponse, Error, void> {
  const invalidate = useInvalidateSuggestions();
  return useMutation({
    mutationFn: async () =>
      suggestionResponseSchema.parse(await api.post(`/api/v1/suggestions/${id}/dismiss`)),
    onSuccess: invalidate,
  });
}
